import { useEffect, useRef, useState } from 'react';
import type { InputSpec, Item } from '@guess/engine';
import { cx } from '../ui/cx';
import type { AnswerProps } from './AnswerRenderer';
import s from './input.module.scss';

type ChoiceSpec = Extract<InputSpec, { kind: 'choice' }>;

/**
 * Le QCM, dont les options s'eliminent au fil des paliers.
 *
 * Le serveur retire simplement les mauvaises options du spec. La tentation
 * serait de les laisser disparaitre : c'est ce qu'il ne faut pas faire.
 * Une option qui s'evapore en silence oblige a relire les quatre restantes
 * pour comprendre laquelle est partie — alors que savoir CE QUI vient d'etre
 * elimine est precisement l'information que le palier vient d'offrir.
 *
 * On garde donc la trace de tout ce qu'on a vu, et on barre ce qui sort. Le
 * parent remonte ce composant a chaque manche, donc cette memoire ne fuit
 * jamais d'une question a la suivante.
 */
export function ChoiceAnswer({ spec, disabled, onSubmit }: AnswerProps<ChoiceSpec>) {
  const [picked, setPicked] = useState<string | null>(null);
  // Toutes les options rencontrees, dans leur ordre d'apparition initial.
  const seenRef = useRef<Item[]>([]);
  const [seen, setSeen] = useState<Item[]>([]);

  useEffect(() => {
    const known = new Set(seenRef.current.map((item) => item.id));
    const added = spec.options.filter((item) => !known.has(item.id));
    if (added.length > 0) {
      seenRef.current = [...seenRef.current, ...added];
      setSeen(seenRef.current);
    }
  }, [spec.options]);

  const alive = new Set(spec.options.map((item) => item.id));
  const list = seen.length > 0 ? seen : spec.options;

  const choose = (optionId: string) => {
    if (disabled || !alive.has(optionId)) return;
    setPicked(optionId);
    onSubmit({ kind: 'choice', optionId });
  };

  return (
    <div className={s.choiceGrid} role="group" aria-label="Choisis une reponse">
      {list.map((item, index) => {
        const eliminated = !alive.has(item.id);
        return (
          <button
            key={item.id}
            type="button"
            className={cx(s.choice, eliminated && s.choiceOut, picked === item.id && s.choicePicked)}
            onClick={() => choose(item.id)}
            disabled={disabled || eliminated}
            aria-label={eliminated ? `${item.label} (eliminee)` : item.label}
          >
            <span className={s.choiceKey} aria-hidden="true">
              {String.fromCharCode(65 + index)}
            </span>
            {item.imageUrl ? <img className={s.choiceThumb} src={item.imageUrl} alt="" /> : null}
            <span className={s.choiceLabel}>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}
